import {ChartType, ChartTypeDisplayName, ChartVizType} from 'common/report/constants';
import type {SectionItem, TooltipInfo} from 'common/components/items-menu/types';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import barSvg from 'common/assets/images/charts/bar.svg';
import lineSvg from 'common/assets/images/charts/line.svg';
import pieSvg from 'common/assets/images/charts/pie.svg';
import stackedBarSvg from 'common/assets/images/charts/stacked-bar.svg';
import stackedLineSvg from 'common/assets/images/charts/stacked-line.svg';
import tableSvg from 'common/assets/images/charts/table.svg';
import funnelFrequencySvg from 'common/assets/images/charts/funnel-frequency.svg';
import funnelStepsSvg from 'common/assets/images/charts/funnel-steps.svg';
import funnelTtcSvg from 'common/assets/images/charts/funnel-ttc.svg';
import funnelTrendsSvg from 'common/assets/images/charts/funnel-trends.svg';
import sankeySvg from 'common/assets/images/charts/sankey.svg';
import retentionTableSvg from 'common/assets/images/charts/retention-table.svg';
import retentionTrendsSvg from 'common/assets/images/charts/retention-trends.svg';
import {useRetentionConsistency} from 'common/report/util/feature-sampling';

const commonTooltipAttrs: TooltipInfo = {
  size: DisplaySize.Small,
  placement: Placement.Left,
};

export const CHART_VIZ_TYPE_ICONS: {[key in ChartVizType]: string} = {
  [ChartVizType.Bar]: `bar`,
  [ChartVizType.Line]: `line`,
  [ChartVizType.Metric]: `single-metric`,
  [ChartVizType.Steps]: `funnel-steps`,
  [ChartVizType.Sankey]: `top-paths`,
};

export const ChartTypeSelection: Partial<{[key in ChartType]: SectionItem}> = {
  [ChartType.Bar]: {
    icon: `bar`,
    label: ChartTypeDisplayName[ChartType.Bar],
    value: ChartType.Bar,
    tooltip: {
      text: `Bars are best to display comparisons between categories.`,
      'image-src': barSvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.Line]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.Line],
    value: ChartType.Line,
    tooltip: {
      text: `Line charts display trends over time.`,
      'image-src': lineSvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.Pie]: {
    icon: `pie`,
    label: ChartTypeDisplayName[ChartType.Pie],
    value: ChartType.Pie,
    tooltip: {
      text: `Pie charts illustrate composition of a whole in pie form.`,
      'image-src': pieSvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.Metric]: {
    icon: `single-metric`,
    label: ChartTypeDisplayName[ChartType.Metric],
    value: ChartType.Metric,
  },
  [ChartType.StackedBar]: {
    icon: `bar-stacked`,
    label: ChartTypeDisplayName[ChartType.StackedBar],
    value: ChartType.StackedBar,
    tooltip: {
      text: `Stacked bar is best when comparing composition of multiple counts.`,
      'image-src': stackedBarSvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.StackedLine]: {
    icon: `stacked-line`,
    label: ChartTypeDisplayName[ChartType.StackedLine],
    value: ChartType.StackedLine,
    tooltip: {
      text: `Stacked line charts display compositions of counts over time.`,
      'image-src': stackedLineSvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.Sankey]: {
    icon: `top-paths`,
    label: ChartTypeDisplayName[ChartType.Sankey],
    value: ChartType.Sankey,
  },
  [ChartType.Table]: {
    icon: `table`,
    label: ChartTypeDisplayName[ChartType.Table],
    value: ChartType.Table,
    tooltip: {
      text: `Tables display data in a tabular view.`,
      'image-src': tableSvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.FunnelSteps]: {
    icon: `funnel-steps`,
    label: ChartTypeDisplayName[ChartType.FunnelSteps],
    value: ChartType.FunnelSteps,
    tooltip: {
      text: `View conversion rates between funnel steps.`,
      'image-src': funnelStepsSvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.FunnelTrend]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.FunnelTrend],
    value: ChartType.FunnelTrend,
    tooltip: {
      text: `View changes in conversion rate over time.`,
      'image-src': funnelTrendsSvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.FunnelFrequencyLine]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.FunnelFrequencyLine],
    value: ChartType.FunnelFrequencyLine,
  },
  [ChartType.FunnelFrequencyBar]: {
    icon: `bar`,
    label: ChartTypeDisplayName[ChartType.FunnelFrequencyBar],
    value: ChartType.FunnelFrequencyBar,
  },
  [ChartType.FunnelTTCBar]: {
    icon: `bar`,
    label: ChartTypeDisplayName[ChartType.FunnelTTCBar],
    value: ChartType.FunnelTTCBar,
  },
  [ChartType.FunnelTTCLine]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.FunnelTTCLine],
    value: ChartType.FunnelTTCLine,
  },
  [ChartType.FunnelMedianTTC]: {
    icon: `bar-stacked`,
    label: ChartTypeDisplayName[ChartType.FunnelMedianTTC],
    value: ChartType.FunnelMedianTTC,
    tooltip: {
      text: `View a distribution of the time it takes users to convert through the funnel.`,
      'image-src': funnelTtcSvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.RetentionLine]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.RetentionLine],
    value: ChartType.RetentionLine,
  },
  [ChartType.RetentionTrend]: {
    icon: `line`,
    label: useRetentionConsistency()
      ? ChartTypeDisplayName[ChartType.Line]
      : ChartTypeDisplayName[ChartType.RetentionTrend],
    value: ChartType.RetentionTrend,
    tooltip: {
      text: `View the retention trend over a period of time.`,
      'image-src': retentionTrendsSvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.RetentionTrendMetric]: {
    icon: `single-metric`,
    // use InsightMetric here, we will eventually use insight API so this is correct
    label: ChartTypeDisplayName[ChartType.Metric],
    value: ChartType.RetentionTrendMetric,
    tooltip: {
      text: `View the retention trend over a period of time.`,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.RetentionTable]: {
    icon: `retention-curve`,
    label: ChartTypeDisplayName[ChartType.RetentionTable],
    value: ChartType.RetentionTable,
    tooltip: {
      text: `View retention rates of individual cohorts of users.`,
      'image-src': retentionTableSvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.Frequency]: {
    icon: `bar`,
    label: ChartTypeDisplayName[ChartType.Frequency],
    value: ChartType.Frequency,
    tooltip: {
      text: `The number of times a user completes a step before converting or dropping off.`,
      'image-src': funnelFrequencySvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.FunnelTopPaths]: {
    icon: `top-paths`,
    label: ChartTypeDisplayName[ChartType.FunnelTopPaths],
    value: ChartType.FunnelTopPaths,
    tooltip: {
      text: `When using an Any Order Funnel, view which paths were most common.`,
      'image-src': sankeySvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.FlowsTopPaths]: {
    icon: `funnel-steps`,
    label: ChartTypeDisplayName[ChartType.FlowsTopPaths],
    value: ChartType.FlowsTopPaths,
    tooltip: {
      text: `Specifically analyze the most often followed sequences of events.`,
      'image-src': funnelStepsSvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.Flows]: {
    icon: `top-paths`,
    label: ChartTypeDisplayName[ChartType.Flows],
    value: ChartType.Sankey, // Alias
    tooltip: {
      text: `Explore a fluid diagram of various user paths.`,
      'image-src': sankeySvg,
      ...commonTooltipAttrs,
    },
  },
  [ChartType.ImpactAdoption]: {
    icon: `line`,
    label: ChartTypeDisplayName[ChartType.ImpactAdoption],
    value: ChartType.ImpactAdoption,
  },
  [ChartType.ImpactTrends]: {
    icon: `stacked-line`,
    label: ChartTypeDisplayName[ChartType.ImpactTrends],
    value: ChartType.ImpactTrends,
  },
  [ChartType.ImpactPropensity]: {
    icon: `bar-stacked`,
    label: ChartTypeDisplayName[ChartType.ImpactPropensity],
    value: ChartType.ImpactPropensity,
  },
};
